import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Modal, Button, Group, Text } from "@mantine/core";
import { LabeledDropDownSelect } from "./DropDownSelect";
import AdminOnlyWarning from "./Settings/AdminOnlyWarning";
import {
	getSharePasswordSettings,
	setSharePasswordSettings,
} from "../actions/tenantActions";

const SHARE_PASSWORD_OPTIONS = [
	{
		value: "optional",
		label: "OPTIONAL: Users can decide whether or not to password protect each share (default)",
	},
	{
		value: "mandatory",
		label: "MANDATORY: Every share will be password protected, users cannot change",
	},
];

function SharePasswordSettings({
	sharePasswordSettingsLoading,
	canEditSettings,
	externalShareIsPasswordProtected,
	getSharePasswordSettings,
	setSharePasswordSettings,
}) {
	const [selectedValue, setSelectedValue] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	useEffect(() => {
		getSharePasswordSettings();
	}, [getSharePasswordSettings]);

	useEffect(() => {
		if (selectedValue === null && !sharePasswordSettingsLoading) {
			setSelectedValue(
				externalShareIsPasswordProtected ? "mandatory" : "optional"
			);
		}
	}, [
		sharePasswordSettingsLoading,
		externalShareIsPasswordProtected,
		selectedValue,
	]);

	const handleDropdownChange = (name, option) => {
		setSelectedValue(option.value);
	};

	const handleApply = () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = () => {
		const isMandatory = selectedValue === "mandatory";
		setSharePasswordSettings({ featureValue: isMandatory })
			.then(() => setConfirmModalOpen(false))
			.catch(() => setConfirmModalOpen(false));
	};

	const handleCancel = () => {
		setSelectedValue(
			externalShareIsPasswordProtected ? "mandatory" : "optional"
		);
	};

	if (sharePasswordSettingsLoading && selectedValue === null) {
		return null;
	}

	const currentOption = SHARE_PASSWORD_OPTIONS.find(
		(o) => o.value === selectedValue
	);
	const savedValue = externalShareIsPasswordProtected
		? "mandatory"
		: "optional";
	const hasChanged = selectedValue !== savedValue;

	return (
		<div style={{ width: 600, paddingTop: 20, paddingLeft: 15 }}>
			<p style={{ marginBottom: 20 }}>
				Choose whether password protection is optional or mandatory for
				all external shares sent by users in your firm
			</p>
			<AdminOnlyWarning
				userCanEditSettings={canEditSettings}
				singular={true}
			/>
			<LabeledDropDownSelect
				label="External Share Password Protection"
				name="externalShareIsPasswordProtected"
				options={SHARE_PASSWORD_OPTIONS}
				value={currentOption || null}
				defaultValue={currentOption || null}
				handler={handleDropdownChange}
				isDisabled={!canEditSettings}
			/>
			{canEditSettings && hasChanged ? (
				<div style={{ marginTop: 20, display: "flex", gap: 8 }}>
					<Button onClick={handleApply}>Apply</Button>
					<Button variant="default" onClick={handleCancel}>
						Cancel
					</Button>
				</div>
			) : null}
			<Modal
				opened={confirmModalOpen}
				onClose={() => setConfirmModalOpen(false)}
				title="Confirm Password Protection Change"
				size="md"
			>
				<Text style={{ marginBottom: 20 }}>
					You are about to change the external share password
					protection setting for all users in your firm. Please
					confirm this is your intent.
				</Text>
				<Group justify="flex-end">
					<Button
						variant="default"
						onClick={() => setConfirmModalOpen(false)}
					>
						Cancel
					</Button>
					<Button onClick={handleConfirm}>Confirm</Button>
				</Group>
			</Modal>
		</div>
	);
}

const mapStateToProps = (state) => ({
	sharePasswordSettingsLoading: state.tenant.sharePasswordSettingsLoading,
	externalShareIsPasswordProtected:
		state.tenant.tenantFeatures.externalShareIsPasswordProtected,
	canEditSettings: state.user.userRights
		? state.user.userRights.UPDATE_ZIP_PASSWORD_SETTINGS
		: false,
});

export default connect(mapStateToProps, {
	getSharePasswordSettings,
	setSharePasswordSettings,
})(SharePasswordSettings);
